import { useCallback, useEffect, useRef, useState, SyntheticEvent } from 'react';
import cn from 'classnames';

import { useClientContext, useFilterAndSearchContext, useTranslationContext, useUserContext } from 'hooks';
import { CookieService, TrackerServiceInstance } from 'services';
import * as interfaces from 'interfaces';

import Icons from '../Icons';
import CategoriesFilter from './CategoriesFilter';
import useClickOutside from 'hooks/useClickOutside.hook';
import SearchBlock from './SearchBlock';
import SignUpModal from 'popups/SignUpModal';
import VolunteerBlock from './VolunteerBlock';
import DonationBlock from './DonationBlock';
import LanguagePicker from './LanguagePicker';

import classes from './Header.module.scss';

import logoSrc from 'assets/logo/Logo-Full.png';

import { getClientOptions } from 'scripts/clients/index'

const { REACT_APP_DEPLOYMENT_ENV } = process.env;
const REPD_HOME_URL = 'https://www.repd.us';

export default function Header() {
  const filterRef = useRef(null);
  const searchRef = useRef(null);
  const languageRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const { client, setCategories, setClient } = useClientContext();
  const { updateUser } = useUserContext();
  const { t, translateCustomText, language } = useTranslationContext();
  const { isOutside: isOutsideFilter, setIsOutside: setIsOutsideFilter } = useClickOutside(filterRef);
  const { isOutside: isOutsideSearch, setIsOutside: setIsOutsideSearch } = useClickOutside(searchRef);
  const { isOutside: isOutsideLanguagePicker, setIsOutside: setIsOutsideLanguagePicker } = useClickOutside(languageRef);
  const { isOutside: isOutsideMobileMenu, setIsOutside: setIsOutsideMobileMenu } = useClickOutside(mobileMenuRef);

  const [isFiltersShown, setIsFiltersShown] = useState(false);
  const [isSearchShown, setIsSearchShown] = useState(false);
  const [isVolunteerShown, setIsVolunteerShown] = useState(false);
  const [isSignUpShown, setIsSignUpShown] = useState(false);
  const [isMobileMenuShown, setIsMobileMenuShown] = useState(false);
  const [isLanguagePickerShown, setIsLanguagePickerShown] = useState(false);

  const customClientOptions = getClientOptions(client) || {};
  const customHeaderLinks = customClientOptions?.links || [];
  const customHeaderHiddenLinks = customClientOptions?.hiddenLinks || [];

  const {
    filter: activeFilters,
    setFilter: setActiveFilters,
    search: activeSearch,
    setSearch: setActiveSearch
  } = useFilterAndSearchContext();

  const handleMobileMenuOpen = (event?: SyntheticEvent) => {
    if (!isMobileMenuShown) {
      event?.stopPropagation();
      setIsFiltersShown(false);
      setIsSearchShown(false);
      setIsMobileMenuShown(true);
    }
  };

  const updateActiveFilters = useCallback(
    (receivedFilters: string[]) => {
      setActiveFilters(receivedFilters);
      setIsFiltersShown(false);
      setIsOutsideFilter(false);

      TrackerServiceInstance?.trackEvent('categoryFilter', { event_category: 'Search', category: receivedFilters.join(',') });
    },
    [setActiveFilters, setIsOutsideFilter, setIsFiltersShown]
  );

  const discardFilterModal = useCallback(() => {
    setActiveFilters([] as string[]);
    setIsFiltersShown(false);
    setIsOutsideFilter(false);
  }, [setActiveFilters, setIsOutsideFilter, setIsFiltersShown]);

  const updateActiveSearch = useCallback(
    (searchValue: string) => {
      setActiveSearch(searchValue.trim());
      setIsSearchShown(false);
      setIsOutsideSearch(false);

      TrackerServiceInstance?.trackEvent('searchFilter', { event_category: 'Filter', searchTerm: searchValue });
    },
    [setActiveSearch, setIsOutsideSearch, setIsSearchShown]
  );

  const discardSearchModal = useCallback(() => {
    setActiveSearch('');
    setIsSearchShown(false);
    setIsOutsideSearch(false);
  }, [setActiveSearch, setIsOutsideSearch, setIsSearchShown]);

  const onClearData = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();

    CookieService.deleteAllCookies();

    setClient({} as interfaces.ClientInterface);
    updateUser({} as interfaces.UserInterface);
    setCategories([]);

    setTimeout(() => {
      // eslint-disable-next-line no-restricted-globals
      location.reload();
    }, 10);
  }, [setCategories, setClient, updateUser]);

  if (isOutsideFilter) {
    setIsFiltersShown(false);
    setIsOutsideFilter(false);
  }

  if (isOutsideSearch) {
    setIsSearchShown(false);
    setIsOutsideSearch(false);
  }

  if (isOutsideLanguagePicker) {
    setIsLanguagePickerShown(false);
    setIsOutsideLanguagePicker(false);
  }

  if (isOutsideMobileMenu) {
    setIsMobileMenuShown(false);
    setIsOutsideMobileMenu(false);
  }

  useEffect(() => {
    if (isFiltersShown || isSearchShown || isMobileMenuShown || isLanguagePickerShown)
      setIsVolunteerShown(false);
  }, [isFiltersShown, isSearchShown, isMobileMenuShown, isLanguagePickerShown]);

  const hiddenLinksArePresent = customHeaderHiddenLinks && customHeaderHiddenLinks.length > 0
  const isHomeHidden      : boolean = hiddenLinksArePresent && customHeaderHiddenLinks.indexOf('home') > -1
  const isVolunteerHidden : boolean = hiddenLinksArePresent && customHeaderHiddenLinks.indexOf('volunteer') > -1
  const isDonateHidden    : boolean = hiddenLinksArePresent && customHeaderHiddenLinks.indexOf('donate') > -1

  const isDentonCity = client.id === '260';
  const isSuisunCity = client.id === '261';

  return (
    <>
      <header className={cn(classes.Header, 'Header')} style={client?.topBarColour ? { backgroundColor: client.topBarColour } : {}}>
        <div className={cn(classes.wrapper, 'wrapper')}>
          <a className={cn(classes.websiteURL, 'websiteURL')}
            href={client?.websiteURL ? client.websiteURL : REPD_HOME_URL} target="_blank" rel="noreferrer"
          >
            {
              isDentonCity ? <img style={{height: 80, position: "relative", top: -47, left: -20}}
                                  src={client?.logoURL ? client.logoURL : logoSrc}
                                  alt="Page logo"/> : isSuisunCity ?
                  <img style={{height: 140, position: "relative", top: -5}}
                       src={client?.logoURL ? client.logoURL : logoSrc}
                       alt="Page logo"/>:
                  <img className={classes.logo} src={client?.logoURL ? client.logoURL : logoSrc} alt="Page logo"/>
            }
          </a>

          <div className={cn(classes.primaryNav, 'primaryNav')}>
            <div className={cn(classes.filter, 'filter')}>
              <div
                  id="categories_button" ref={filterRef}
                className={cn(classes.subFilter, 'subfilter', classes.categories, isFiltersShown && classes.active + ' active')}
              >
                <div
                  className={classes.menuButton}
                  onClick={() => setIsFiltersShown(!isFiltersShown)}
                >
                  <Icons iconType="filter" iconClass={classes.filterIcon} />
                  <div className={classes.text}>{client?.clientType === 'Campaign' ? t('Issues') : t('Categories')}</div>
                </div>
                {isFiltersShown &&
                  <CategoriesFilter
                    activeFilters={activeFilters}
                    onSubmitFilter={updateActiveFilters}
                    onDiscardFilter={discardFilterModal}
                  />}
              </div>

              <div
                ref={searchRef}
                className={cn(classes.subFilter, 'subfilter', classes.search, isSearchShown && classes.active + ' active')}
              >
                <div
                  className={classes.menuButton}
                  onClick={() => setIsSearchShown(!isSearchShown)}
                >
                  <Icons iconType="search" iconClass={classes.filterIcon} />
                  <div className={classes.text}>{t('Search')}</div>
                </div>
                {isSearchShown &&
                  <SearchBlock
                    activeSearch={activeSearch}
                    onSubmitSearch={updateActiveSearch}
                    onDiscardSearch={discardSearchModal}
                  />}
              </div>

              <Icons
                iconType={isMobileMenuShown ? "times" : "bars" }
                iconClass={isMobileMenuShown ? cn(classes.menuClose, 'menuClose') : cn(classes.menuOpen, 'menuOpen')}
                handleClick={(event) => handleMobileMenuOpen(event)}
              />
            </div>

            <div
              ref={mobileMenuRef}
              className={cn(classes.mainNavigation, 'mobileMenu', isMobileMenuShown && classes.navigationVisible)}
            >
              {(!hiddenLinksArePresent || !isHomeHidden) && <span className={cn(classes.navLink, 'home', classes.active)}>
                {translateCustomText("headerHomeLinkText", t("Home"))}
              </span>}

              {customHeaderLinks.map((option: any, index: number) => {
                option.url = option.url.replace( /VOLUNTEER_URL/, client.volunteerURL ).replace( /DONATE_URL/, client.donationURL );

                return <a key={index} className={cn(classes.navLink, 'navlink', option.customClass)} href={option.url}>
                  {option.label}
                  {option.customDropdownLinks &&
                    <object className={option.customDropdownClass}>
                      {option.customDropdownLinks.map((link: {label: string, url: string}) => <a key={link.url} href={link.url} target="_blank">{link.label}</a>)}
                    </object>
                  }
                </a>
              })}

              {(!hiddenLinksArePresent || !isVolunteerHidden) && <VolunteerBlock
                volunteerURL={client?.volunteerURL}
                isOpened={isVolunteerShown}
                setIsOpened={setIsVolunteerShown}
              />}

              {(!hiddenLinksArePresent || !isDonateHidden) && <DonationBlock donationURL={client?.donationURL} />}

              <div
                ref={languageRef}
                className={cn(classes.languagePicker, isMobileMenuShown && classes.navLink)}
                onClick={() => setIsLanguagePickerShown(!isLanguagePickerShown)}
              >
                <div>{t("Translate")}</div>
                <Icons iconType='caret-down' iconClass={classes.languagePickerIcon} />

              </div>

              {isLanguagePickerShown && <LanguagePicker/>}

              {/* TODO: remove display: none when resume work on sign up functionality */}
              <span
                className={cn(classes.navLink, classes.signInHidden)}
                onClick={() => setIsSignUpShown(true)}
                style={{ display: 'none' }}
              >
                Sign Up
              </span>

              {REACT_APP_DEPLOYMENT_ENV === 'state' && (
                <span className={cn(classes.navLink, classes.showOnStaging)} onClick={() => onClearData()}>
                  Clear Data
                </span>
              )}

              <div className="account sign-in-shown hidden" style={{ display: 'none' }}>
                <i className="fas fa-user-circle user-avatar"></i>
                <i className="fas fa-sort-down arrow"></i>
                <div className="sub-nav two-items">
                  <span data-route="profile">My Profile</span>
                  <span data-function="signOut()">Sign Out</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {isSignUpShown ? <SignUpModal handleClose={() => setIsSignUpShown(false)} /> : null}
    </>
  );
}
